/**
 * TDBShopifyUnallocated — Britto's inbox of Shopify orders that fell
 * through to "Unallocated" (no routing rule matched). Each row shows
 * order details and the shipping address — Britto resolves by either
 * adding a new routing rule (out of scope for this tile) or contacting
 * Aditya.
 *
 * The sync-health chip in the header shows when Shopify last delivered
 * a paid order (green <2h, amber 2-6h, red >6h).
 */
import { useEffect, useState, useCallback } from 'react';
import { ShoppingCart, AlertTriangle, RefreshCw, Inbox } from 'lucide-react';
import api from '@/lib/api';

function fmtRupees(n) {
  if (!n) return '₹0';
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
}

const HEALTH_TONE = {
  green: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  amber: 'bg-amber-50 text-amber-800 border-amber-200',
  red: 'bg-red-50 text-red-800 border-red-200',
  no_data: 'bg-gray-50 text-gray-600 border-gray-200',
};

export default function TDBShopifyUnallocated({ className = '' }) {
  const [health, setHealth] = useState(null);
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const [h, u] = await Promise.all([
        api.get('/tdb-mis/sync-health'),
        api.get('/tdb-mis/unallocated', { params: { limit: 30 } }),
      ]);
      setHealth(h.data);
      setList(u.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load Shopify status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const level = health?.level || 'no_data';
  const minutes = health?.minutes_since;
  const orders = list?.orders || [];
  const total = list?.unallocated_total || 0;

  return (
    <div
      data-testid="tdb-shopify-unallocated"
      className={`bg-white rounded-2xl border border-gray-200 p-6 ${className}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ShoppingCart className="w-5 h-5 text-gray-700" />
          <h3 className="text-base font-semibold text-gray-900">Shopify Inbox</h3>
          <span
            data-testid="tdb-shopify-sync-health"
            className={`text-xs px-2 py-0.5 rounded-full border ${HEALTH_TONE[level] || HEALTH_TONE.no_data}`}
            title={health?.message || ''}
          >
            {level === 'no_data' ? 'No data' : `${level.toUpperCase()} · last ${Math.round(minutes || 0)}m ago`}
          </span>
        </div>
        <button
          data-testid="tdb-shopify-refresh"
          onClick={load}
          className="text-xs px-2 py-1 rounded-md border border-gray-300 bg-white hover:bg-gray-50"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {orders.length > 0 ? (
        <>
          <div
            data-testid="tdb-shopify-unallocated-banner"
            className="mb-3 p-2 rounded-md bg-red-50 border border-red-200 text-xs text-red-900 flex items-start gap-1.5"
          >
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <div>
              <span className="font-semibold">{orders.length} Shopify orders · {fmtRupees(total)} unallocated</span>{' '}
              · routing rules didn't match. Add a rule or tag the city manually.
            </div>
          </div>
          <div className="space-y-1 max-h-72 overflow-y-auto" data-testid="tdb-shopify-unallocated-list">
            {orders.map((o, i) => (
              <div
                key={i}
                data-testid={`tdb-shopify-unallocated-row-${i}`}
                className="flex items-start justify-between text-xs px-2 py-1.5 rounded-md hover:bg-gray-50 border border-gray-100"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-gray-900">#{o.order_number}</div>
                  <div className="text-gray-500 text-[11px] truncate">
                    {o.shipping_city || '—'} · {o.shipping_zip || 'no zip'} · matched rule: <span className="font-mono">{o.rule}</span>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-semibold text-gray-900">{fmtRupees(o.total)}</div>
                  <div className="text-[10px] text-gray-400">{(o.shopify_created_at || '').slice(0, 10)}</div>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div data-testid="tdb-shopify-unallocated-empty" className="text-center py-6 text-xs text-emerald-700 bg-emerald-50 rounded-md border border-emerald-200">
          <Inbox className="w-6 h-6 mx-auto mb-1 text-emerald-600" />
          All Shopify orders routed cleanly. Nothing for Britto to resolve.
        </div>
      )}

      {err && (
        <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-2">
          {err}
        </div>
      )}
    </div>
  );
}
